/**
 * Media Engine Types
 * 
 * Unified interface for media playback across platforms.
 * Abstracts Vidstack (Web) and @capgo/native-audio (Mobile).
 */

// ============================================
// Media Source Types
// ============================================

export type MediaSourceType = "hls" | "mp4" | "audio" | "unknown";

export interface MediaSource {
	/** URL or local file path */
	src: string;
	/** Source type for proper handling */
	type: MediaSourceType;
	/** Whether this is a local file (offline) or remote URL (streaming) */
	isLocal: boolean;
}

// ============================================
// Playback State
// ============================================

export type PlaybackState = "idle" | "loading" | "ready" | "playing" | "paused" | "ended" | "error";

export interface PlaybackPosition {
	/** Current position in seconds */
	currentTime: number;
	/** Total duration in seconds */
	duration: number;
	/** Playback rate (1.0 = normal) */
	playbackRate: number;
}

// ============================================
// Media Metadata (for lock screen / notifications)
// ============================================

export interface MediaMetadata {
	title: string;
	artist?: string;
	album?: string;
	artwork?: Array<{
		src: string;
		sizes?: string;
		type?: string;
	}>;
}

// ============================================
// Engine Configuration
// ============================================

export interface MediaEngineConfig {
	/** Enable background playback (mobile) */
	backgroundMode?: boolean;
	/** Show notification controls (mobile) */
	showNotification?: boolean;
	/** Media type: audio or video */
	mediaType: "audio" | "video";
	/** Initial volume (0-1) */
	volume?: number;
	/** Whether to loop playback */
	loop?: boolean;
}

// ============================================
// Event Callbacks
// ============================================

export interface MediaEngineCallbacks {
	/** Called when playback state changes */
	onStateChange?: (state: PlaybackState) => void;
	/** Called periodically with current position */
	onTimeUpdate?: (position: PlaybackPosition) => void;
	/** Called when media has ended */
	onEnded?: () => void;
	/** Called when an error occurs */
	onError?: (error: Error) => void;
	/** Called when media is ready to play */
	onReady?: (duration: number) => void;
	/** Called when loading starts */
	onLoadStart?: () => void;
	/** Called when media can start playing */
	onCanPlay?: () => void;
}

// ============================================
// Media Engine Interface
// ============================================

export interface IMediaEngine {
	/**
	 * Load a media source for playback
	 * @param source - The media source to load
	 * @param metadata - Metadata for lock screen / notifications
	 */
	load(source: MediaSource, metadata?: MediaMetadata): Promise<void>;

	/**
	 * Start or resume playback
	 */
	play(): Promise<void>;

	/**
	 * Pause playback
	 */
	pause(): Promise<void>;

	/**
	 * Seek to a specific position
	 * @param time - Position in seconds
	 */
	seek(time: number): Promise<void>;

	/**
	 * Stop playback and reset position
	 */
	stop(): Promise<void>;

	/**
	 * Get current playback position
	 */
	getPosition(): PlaybackPosition;

	/**
	 * Get current playback state
	 */
	getState(): PlaybackState;

	/**
	 * Set playback volume
	 * @param volume - Volume level (0-1)
	 */
	setVolume(volume: number): void;

	/**
	 * Set playback rate
	 * @param rate - Playback rate (1.0 = normal)
	 */
	setPlaybackRate(rate: number): void;

	/**
	 * Update metadata (for lock screen / notifications)
	 */
	updateMetadata(metadata: MediaMetadata): void;

	/**
	 * Register event callbacks
	 */
	setCallbacks(callbacks: MediaEngineCallbacks): void;

	/**
	 * Clean up resources
	 */
	destroy(): void;

	/**
	 * Whether this engine is for native platforms
	 */
	readonly isNative: boolean;

	/**
	 * Current media source (if loaded)
	 */
	readonly currentSource: MediaSource | null;
}

// ============================================
// Engine Factory Types
// ============================================

export type MediaEngineType = "web" | "native";

export interface MediaEngineOptions {
	/** Configuration for the engine */
	config: MediaEngineConfig;
	/** Initial callbacks */
	callbacks?: MediaEngineCallbacks;
}

// ============================================
// Helper type for source creation
// ============================================

/**
 * Create a MediaSource from a URL
 */
export function createMediaSource(
	source: string,
	options?: {
		isLocal?: boolean;
		forceType?: MediaSourceType;
	}
): MediaSource {
	const isLocal = options?.isLocal ?? (
		source.startsWith("file://") ||
		source.startsWith("capacitor://") ||
		source.startsWith("http://localhost/_capacitor_file_")
	);

	let type: MediaSourceType = options?.forceType ?? "unknown";
	
	if (!options?.forceType) {
		if (source.includes(".m3u8") || source.includes("application/x-mpegurl")) {
			type = "hls";
		} else if (source.includes(".mp4") || source.includes(".m4a") || source.includes(".m4v")) {
			type = "mp4";
		} else if (source.includes(".mp3") || source.includes(".wav") || source.includes(".aac")) {
			type = "audio";
		}
	}

	return { src: source, type, isLocal };
}

/**
 * Create HLS source for streaming
 */
export function createHlsSource(url: string): MediaSource {
	return {
		src: url,
		type: "hls",
		isLocal: false,
	};
}

/**
 * Create local file source
 */
export function createLocalSource(path: string, mediaType: "audio" | "video" = "video"): MediaSource {
	return {
		src: path,
		type: mediaType === "audio" ? "audio" : "mp4",
		isLocal: true,
	};
}
